import { useState, useMemo } from "react";
import { Building2, Bitcoin, BarChart3, Coins, Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Asset, StockAsset, EtfAsset, FundAsset, CryptoAsset } from "@/schemas";

interface AssetLogoProps {
  asset: Asset | { type: string; symbol: string; website?: string; coin_image_url?: string };
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
  showFallbackIcon?: boolean;
}

const sizeClasses = {
  xs: "h-5 w-5",
  sm: "h-6 w-6",
  md: "h-8 w-8",
  lg: "h-10 w-10",
  xl: "h-12 w-12",
};

const iconSizeClasses = {
  xs: "h-3 w-3",
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
  lg: "h-5 w-5",
  xl: "h-6 w-6",
};

const sizePx = {
  xs: 20,
  sm: 24,
  md: 32,
  lg: 40,
  xl: 48,
};

/**
 * Extract domain from a URL or website string
 */
function extractDomain(website: string): string | null {
  try {
    // Handle URLs without protocol
    const urlString = website.startsWith("http") 
      ? website 
      : `https://${website}`;
    const url = new URL(urlString);
    // Remove www. prefix if present
    return url.hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/**
 * Get Logo.dev URL from a domain
 * Logo.dev API: https://docs.logo.dev/
 * Requires VITE_LOGO_DEV_TOKEN environment variable
 */
function getLogoDevUrl(domain: string, size: number = 80): string | null {
  const token = import.meta.env?.VITE_LOGO_DEV_TOKEN;
  if (!token) {
    // No token configured, fallback to icon
    return null;
  }
  return `https://img.logo.dev/${domain}?token=${token}&size=${size}&format=png`;
}

/**
 * Get fallback icon component based on asset type
 */
function FallbackIcon({ 
  type, 
  size = "md",
  className,
}: { 
  type: string; 
  size: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
}) {
  const iconClass = cn(iconSizeClasses[size], "text-muted-foreground", className);
  
  switch (type) {
    case "stock":
      return <Building2 className={iconClass} />;
    case "etf":
      return <BarChart3 className={iconClass} />;
    case "fund":
      return <BarChart3 className={iconClass} />;
    case "crypto":
      return <Bitcoin className={iconClass} />;
    case "fiat":
      return <Coins className={iconClass} />;
    case "index":
      return <Globe className={iconClass} />;
    default:
      return <Building2 className={iconClass} />;
  }
}

/**
 * AssetLogo Component
 * 
 * Displays a company/asset logo using:
 * - Direct coin image URL for crypto when available
 * - Logo.dev API for assets with a website (requires VITE_LOGO_DEV_TOKEN)
 * - Fallback icon for assets without logos or on error
 * 
 * @example
 * <AssetLogo asset={asset} size="md" />
 */
export function AssetLogo({ 
  asset, 
  size = "md", 
  className,
  showFallbackIcon = true,
}: AssetLogoProps) {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Determine logo URL for asset:
  // - crypto: prefer coin_image_url, fallback to website/logo.dev
  // - stock/etf: website/logo.dev
  const logoUrl = useMemo(() => {
    if (asset.type === "crypto") {
      const cryptoAsset = asset as CryptoAsset;
      if (cryptoAsset.coin_image_url) {
        return cryptoAsset.coin_image_url;
      }
    }

    if (asset.type !== "stock" && asset.type !== "etf" && asset.type !== "fund" && asset.type !== "crypto") {
      return null;
    }

    const website = (asset as StockAsset | EtfAsset | FundAsset | CryptoAsset).website;
    if (!website) {
      return null;
    }

    const domain = extractDomain(website);
    if (!domain) {
      return null;
    }

    // Request 2x size for retina displays
    return getLogoDevUrl(domain, sizePx[size] * 2);
  }, [asset, size]);

  const containerClass = cn(
    "relative flex items-center justify-center rounded-lg overflow-hidden bg-muted flex-shrink-0",
    sizeClasses[size],
    className
  );

  // No logo URL available - show fallback
  if (!logoUrl || hasError) {
    if (!showFallbackIcon) {
      return null;
    }
    return (
      <div className={containerClass}>
        <FallbackIcon type={asset.type} size={size} />
      </div>
    );
  }

  return (
    <div className={containerClass}>
      {/* Loading placeholder */}
      {isLoading && (
        <div className="absolute inset-0 animate-pulse bg-muted" />
      )}
      
      {/* Logo image */}
      <img
        src={logoUrl}
        alt={`${asset.symbol} logo`}
        width={sizePx[size]}
        height={sizePx[size]}
        className={cn(
          "object-contain transition-opacity duration-200",
          isLoading ? "opacity-0" : "opacity-100"
        )}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setHasError(true);
          setIsLoading(false);
        }}
      />
    </div>
  );
}

/**
 * Hook to get logo URL for an asset
 * Useful when you need just the URL without the component
 */
export function useAssetLogoUrl(asset: Asset | null): string | null {
  return useMemo(() => {
    if (!asset) return null;
    if (asset.type === "crypto" && asset.coin_image_url) return asset.coin_image_url;
    if (asset.type !== "stock" && asset.type !== "etf" && asset.type !== "fund" && asset.type !== "crypto") return null;
    
    const website = (asset as StockAsset | EtfAsset | FundAsset | CryptoAsset).website;
    if (!website) return null;
    
    const domain = extractDomain(website);
    if (!domain) return null;
    
    return getLogoDevUrl(domain);
  }, [asset]);
}
